import { z } from "zod";
import type { TsrTeam } from "./socket";

/**
 * TSR target sports — ARCHERY and SHOOTING — on the wire. Ported from the
 * predecessor backend (Wsl-Hono-BE `src/socket/tsr/archery`, `…/shooting`)
 * with the event names, rooms, keys and payload shapes UNCHANGED, so the TSR
 * client's socket code (and the venue Shooting Interface App's envelope)
 * ports as it is.
 *
 * ── Archery: socket in, socket + REST out ───────────────────────────────────
 *   join_archery { meetId }          → ack { success, rev, snapshot, degraded }   room archery_<meetId>
 *   leave_archery { meetId }
 *   tsr_archery_state  TsrArcheryStateEnvelope → ack { success, stale? , degraded? } relayed to the room
 *   join_archery_timing / leave_archery_timing                                     room archery_timing_<meetId>
 *   tsr_archery_timing_state TsrArcheryTimingEnvelope → same acks, relayed to the timing room
 *   GET /api/tsr/archery/live | /:meetId/state | /:meetId/scoreboard
 *
 * ── Shooting: HTTP in (the range's file watcher), socket + REST out ─────────
 *   POST /api/tsr/shooting/:meetId/state   TsrShootingFeedEnvelope (a SERVICE
 *        token of kind INGEST scoped to shooting — the old `x-api-key` is gone)
 *   join_shooting / leave_shooting { meetId } → ack { success, rev, snapshot, degraded } room shooting_<meetId>
 *   tsr_shooting_state (server → room only; there is no socket ingest)
 *   GET /api/tsr/shooting/live | /:meetId/state | /:meetId/scoreboard
 *
 * Socket payloads and the ingest body keep v1's camelCase (wire compatibility,
 * like ./socket.ts); the REST reads go through `ok()` and are snake_case.
 *
 * NOTHING HERE REACHES POSTGRES. Live state is Redis only (24 h); the result
 * is saved when the operator presses Save Meet — the TSR client maps the
 * state with ./rounds-mapping.ts and calls the EXISTING
 * `PATCH /api/meets/:id/results` (ROUNDS family) as the signed-in operator.
 */

/** `sports.code` of archery / shooting (SEAG seed) — what `/api/tsr/archery` and `/api/tsr/shooting` serve. */
export const TSR_ARCHERY_SPORT_CODE = "ARC";
export const TSR_SHOOTING_SPORT_CODE = "SHO";

/** Live state TTLs (seconds): `archery_state:<id>`, `archery_timing_state:<id>`, `shooting_state:<id>`. */
export const TSR_TARGET_STATE_TTL_SECONDS = 24 * 60 * 60;

// ─── Archery ─────────────────────────────────────────────────────────────────

/** A keypad face: "X" (inner 10) | "10".."1" | "M" (miss). */
export const TSR_ARCHERY_ARROW_VALUES = ["X", "10", "9", "8", "7", "6", "5", "4", "3", "2", "1", "M"] as const;
export type TsrArcheryArrowValue = (typeof TSR_ARCHERY_ARROW_VALUES)[number];

/** Points of an arrow: X counts 10, M 0, anything unreadable 0. */
export const tsrArrowPoints = (value: string): number => (value === "X" ? 10 : value === "M" ? 0 : Number.parseInt(value, 10) || 0);

export interface TsrArcheryArrow {
	value: string;
	/** The referee's "inconclusive" mark — never changes the value. */
	uncertain?: boolean;
}

/** A banked set / end. */
export interface TsrArcherySet {
	a: number;
	b: number;
	arrowsA: TsrArcheryArrow[];
	arrowsB: TsrArcheryArrow[];
	setPointsA: number;
	setPointsB: number;
	winner: TsrTeam | null;
	/** The shoot-off decider (the slot past the format's sets). */
	shootOff?: boolean;
}

export interface TsrArcheryFormat {
	bow: string;
	discipline: string;
	distance?: number;
	arrowsPerSet: number;
	sets: number;
	scoring: "set-points" | "cumulative";
	unit: string;
	setPointsToWin?: number;
	label: string;
	disciplineLabel?: string;
}

/**
 * The part of TSR's `ArcheryState` (archery.engine.ts) the server and the
 * Save Meet mapper read. Structural: everything else TSR ships (display view,
 * clocks, wind) is stored and relayed verbatim.
 */
export interface TsrArcheryState {
	format: TsrArcheryFormat;
	currentSet: number;
	completedSets: TsrArcherySet[];
	arrowsA: TsrArcheryArrow[];
	arrowsB: TsrArcheryArrow[];
	setPointsA: number;
	setPointsB: number;
	matchElapsed?: number;
	matchRunning?: boolean;
	matchFinished: boolean;
	eventFinished?: boolean;
	shootOff?: boolean;
	windSpeed?: string;
	[field: string]: unknown;
}

/** Team identity folded into every envelope by the controller (the server needs no lookup to serve the feed). */
export interface TsrArcheryFeedTeam {
	code: string;
	name: string;
	flag: string | null;
	players: string[];
	color: string;
}

export interface TsrArcheryStreamMeta {
	eventName: string;
	stage: string;
	venue: string;
	meetNo: string;
	court: string;
	gender?: string | null;
	teams: { A: TsrArcheryFeedTeam; B: TsrArcheryFeedTeam };
}

/** `tsr_archery_state` — what the scoring controller emits. */
export interface TsrArcheryStateEnvelope {
	meetId: string;
	rev: number;
	state: TsrArcheryState;
	meta: TsrArcheryStreamMeta;
}

/** What `archery_state:<id>` holds and the room / `join_archery` ack relay: the envelope + a server stamp. */
export interface TsrStoredArcheryState extends TsrArcheryStateEnvelope {
	updatedAt: string;
}

/** The Director of Shooting clock (archery.timing.ts `TimingState`), read structurally. */
export interface TsrArcheryTimingState {
	mode: string;
	serveSide: TsrTeam;
	activeSide: TsrTeam;
	phase: string;
	remaining: number;
	running: boolean;
	arrowNumber: number;
	[field: string]: unknown;
}

/** `tsr_archery_timing_state` — the timing controller's own feed (its own key, room and rev). */
export interface TsrArcheryTimingEnvelope {
	meetId: string;
	rev: number;
	state: TsrArcheryTimingState;
}

export interface TsrStoredArcheryTimingState extends TsrArcheryTimingEnvelope {
	updatedAt: string;
}

export type TsrTargetFeedStatus = "upcoming" | "live" | "finished";

// ─── Shooting (the venue file watcher's envelope) ────────────────────────────

/** Caps on the ingest body: a range session is a few documents of ≤ 100 shooters. */
export const TSR_SHOOTING_DOCUMENTS_MAX = 32;
export const TSR_SHOOTING_ROWS_MAX = 400;
export const TSR_SHOOTING_SCORES_MAX = 64;

const text = (max: number) => z.string().trim().max(max);
const optText = (max: number) => text(max).nullable().optional();
const optInt = () => z.number().int().min(0).max(1_000_000).nullable().optional();
/** A score cell: the decimal TEXT the range software wrote ("104.2", "98"). */
const SCORE_KEY = /^[a-z][a-z0-9_]{0,39}$/;

const shootingAthlete = z.object({
	name: optText(200),
	bib: optText(40),
	competitionBib: optText(40),
	organisation: optText(200),
});

const shootingRow = z.object({
	key: text(80),
	rank: z.number().int().min(1).max(9999).nullable().optional(),
	name: optText(200),
	organisation: optText(200),
	bib: optText(40),
	competitionBib: optText(40),
	isTeam: z.boolean().optional(),
	athletes: z.array(shootingAthlete).max(12).optional(),
	result: optText(40),
	/** DNS / DNF / DSQ / RuledOut, as the ODF wrote it. */
	irm: optText(20),
	eliminated: z.boolean().nullable().optional(),
	shotCount: optInt(),
	shotsFired: optInt(),
	firingPoint: optText(20),
	startOrder: optInt(),
	scores: z
		.record(z.string().regex(SCORE_KEY), text(40))
		.refine((s) => Object.keys(s).length <= TSR_SHOOTING_SCORES_MAX, { message: `At most ${TSR_SHOOTING_SCORES_MAX} score cells` })
		.optional(),
	/** The slot (`meet_competitor_id`) the publisher resolved this row to; null = unmatched. */
	meetParticipantId: z.string().uuid().nullable().optional(),
	matchSource: optText(20),
});

const shootingEvent = z.object({
	code: optText(60),
	name: optText(200),
	phase: optText(60),
	phaseDisplay: optText(60),
	gender: optText(20),
	family: text(20).optional(),
	isTeam: z.boolean().optional(),
	productType: optText(60),
	progressStatus: optText(40),
	resultStatus: optText(40),
	startDate: optText(40),
});

const shootingDocument = z.object({
	id: text(300),
	name: text(300).optional(),
	event: shootingEvent.optional(),
	rows: z.array(shootingRow).max(TSR_SHOOTING_ROWS_MAX),
});

/**
 * `POST /api/tsr/shooting/:meetId/state` — the Shooting Interface App's feed
 * envelope, v1's shape (camelCase on purpose: the publisher is unchanged).
 * Unknown keys are stripped, never stored; every string and array is bounded.
 */
export const tsrShootingFeedEnvelope = z.object({
	meetId: z.string().uuid().nullable().optional(),
	meetName: optText(200),
	/** The publishing PROCESS: a restarted publisher begins its revs at 0 again and must be trusted. */
	instance: optText(80),
	startedAt: optText(40),
	rev: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
	updatedAt: optText(40),
	mapped: optInt(),
	unmapped: optInt(),
	documents: z.array(shootingDocument).max(TSR_SHOOTING_DOCUMENTS_MAX),
});
export type TsrShootingFeedEnvelope = z.output<typeof tsrShootingFeedEnvelope>;
export type TsrShootingDocument = TsrShootingFeedEnvelope["documents"][number];
export type TsrShootingRow = TsrShootingDocument["rows"][number];

/** `?document=` / `?phase=` on the ingest: publish only the documents of THIS meet. */
export const tsrShootingIngestQuery = z.object({
	document: z.string().trim().min(1).max(300).optional(),
	phase: z.string().trim().min(1).max(60).optional(),
});

/** What `shooting_state:<id>` holds and `tsr_shooting_state` relays. */
export type TsrStoredShootingState = TsrShootingFeedEnvelope & { meetId: string; receivedAt: string };

/** `POST /api/tsr/shooting/:meetId/state` → `ok(...)`. */
export interface TsrShootingIngestResponse {
	meet_id: string;
	accepted: boolean;
	/** Set when `accepted` is false. */
	reason?: "stale";
	rev: number;
	status: TsrTargetFeedStatus;
	/** Rows naming a slot of THIS meet. */
	addressed: number;
	/** Rows with no slot id. */
	unaddressed: number;
	/** Slot ids that are not a filled slot of this meet — never applied to anyone. */
	foreign: string[];
	/** A slot listed by more than one document (a qualification and its final both selected). */
	conflicts: { meet_participant_id: string; documents: string[] }[];
	/** The store could not be read or written; the relay still ran. */
	degraded: boolean;
}
